import { useEffect, useState } from "react";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import EmptyTableMessage from "../../components/table/EmptyTableMessage";
import { formatCurrency, formatNumber } from "../../utils/utils_number";

export function DetalleComprobanteTabla({ data, handleEdit, handleDelete, tipoFactura }) {
  const columns = [
    { accessorKey: "numItem", header: "No." },
    { accessorKey: "cantidad", header: "CANT.", cell: ({ getValue }) => formatNumber(getValue(), 0) },
    // { accessorKey: "uniMedida", header: "U. DE MEDIDA" },
    { accessorKey: "descripcion", header: "DESCRIPCION" },
    { accessorKey: "precioUni", header: "P. UNITARIO", cell: ({ getValue }) => formatCurrency(getValue()) },
    tipoFactura !== "11" && {
      accessorKey: "ventaExenta",
      header: "V. EXENTAS",
      cell: ({ getValue }) => formatCurrency(getValue()),
    },
    tipoFactura !== "11" && {
      accessorKey: "ventaNoSuj",
      header: "V. NO SUJETAS",
      cell: ({ getValue }) => formatCurrency(getValue()),
    },
    { accessorKey: "ventaGravada", header: "V. SUJETAS", cell: ({ getValue }) => formatCurrency(getValue()) },
  ].filter(Boolean);

  if (handleEdit || handleDelete) {
    columns.push({
      accessorKey: "acciones",
      header: "",
      cell: ({ row }) => (
        <>
          {handleEdit && (
            <button className="btn btn-xs btn-primary" onClick={() => handleEdit(row.index)}>
              <i className="fa fa-edit"></i>
            </button>
          )}
          {handleDelete && (
            <button className="btn btn-xs btn-danger ms-1" onClick={() => handleDelete(row.index)}>
              <i className="fa fa-times"></i>
            </button>
          )}
        </>
      ),
    });
  }

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="table-responsive">
      <table className="table table-thead-sticky table-tfoot-sticky table-tbody-bordered table-px-10px table-py-4px table-sm table-striped text-nowrap mb-0">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="text-nowrap">
              {headerGroup.headers.map((header) => (
                <th key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowCount() > 0 ? (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="breakWord">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <EmptyTableMessage columns={table.getAllColumns().length} />
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
    Tipo factura: 01 | 03 | 05
    01: Factura consumidor final 
    03: Credito Fiscal
    05: Nota de credito
    11: Factura exportacion
 */
export default function EditableInvoiceTable({ data, setData, tipoFactura, comprobanteRelacionado = null, itemBase, formatearItem }) {
  const [editingRow, setEditingRow] = useState(null);
  const [formValues, setFormValues] = useState(itemBase);

  // value: 1 | label: venta sujeta
  // value: 2 | label: venta no sujeta
  // value: 3 | label: venta exenta
  const [tipoVenta, setTipoVenta] = useState("1");

  useEffect(() => {
    setFormValues(itemBase);
  }, [tipoFactura]);

  const handleEdit = (index) => {
    const item = data[index];
    setEditingRow(index);
    setTipoVenta(item.ventaNoSuj > 0 ? "2" : item.ventaExenta > 0 ? "3" : "1");
    setFormValues(item);
  };

  const handleDelete = (index) => {
    setData((prevData) => prevData.filter((_, i) => i !== index).map((e, i) => ({ ...e, numItem: i + 1 })));
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setFormValues(itemBase);
  };

  const handleSave = () => {
    if (!validarCampos()) return;
    setData((prevData) => {
      const newData = [...prevData];
      newData[editingRow] = formatearItem(formValues, tipoVenta, comprobanteRelacionado, editingRow + 1);
      return newData;
    });
    cancelEdit();
  };

  const handleAdd = () => {
    if (!validarCampos()) return;
    const nuevoItem = formatearItem(formValues, tipoVenta, comprobanteRelacionado, data.length + 1);
    setData((prevData) => [...prevData, nuevoItem]);
    cancelEdit();
  };

  const validarCampos = () => {
    const cantidad = parseFloat(formValues.cantidad) || 0;
    const precioUni = parseFloat(formValues.precioUni) || 0;
    const descripcion = formValues.descripcion.trim(); // Quita espacios en blanco

    if (cantidad <= 0) {
      alert("La cantidad debe ser mayor que 0.");
      return false;
    }

    if (descripcion.length < 1) {
      alert("La descripción no puede estar vacía.");
      return false;
    }

    if (precioUni <= 0) {
      alert("El precio unitario debe ser mayor que 0.");
      return false;
    }
    return true; // Si todo está correcto, retorna true
  };

  return (
    <div>
      <div className="row">
        <div className="col-12 col-md-1 mb-2">
          <label className="form-label">CANTIDAD</label>
          <input
            min="0"
            type="number"
            className="form-control"
            value={formValues.cantidad}
            onChange={(e) =>
              setFormValues((prev) => ({
                ...prev,
                cantidad: e.target.value,
              }))
            }
            onKeyDown={(e) => (e.key === "-" || e.key === "e") && e.preventDefault()}
          />
        </div>
        {/* <div className="col-2 col-md-1 mb-2">
        <label className="form-label">U. MEDIDA</label>
        <input
          type="number"
          className="form-control"
          value={formValues.uniMedida}
          onChange={(e) => setFormValues({ ...formValues, uniMedida: e.target.value })}
        />
      </div> */}
        <div className="col-12 col-md-4 mb-2">
          <label className="form-label">DESCRIPCION</label>
          <input
            type="text"
            className="form-control"
            value={formValues.descripcion}
            onChange={(e) => setFormValues((prev) => ({ ...prev, descripcion: e.target.value }))}
          />
        </div>
        <div className="col-6 col-md-2 mb-2">
          <label className="form-label">P. UNITARIO</label>
          <input
            type="number"
            className="form-control"
            value={formValues.precioUni}
            onChange={(e) =>
              setFormValues((prev) => ({
                ...prev,
                precioUni: e.target.value,
              }))
            }
            onKeyDown={(e) => (e.key === "-" || e.key === "e") && e.preventDefault()}
          />
        </div>
        <div className="col-6 col-md-2 mb-2">
          <label className="form-label">Tipo Venta</label>
          <select className="form-select" value={tipoVenta} onChange={(e) => setTipoVenta(e.target.value)}>
            <option value="1">Venta Gravada</option>
            {tipoFactura !== "11" && <option value="2">Venta No Gravada</option>}
            {tipoFactura !== "11" && <option value="3">Venta Exenta</option>}
          </select>
        </div>
        <div className="col-12 col-md-2 mb-2">
          {editingRow !== null ? (
            <>
              <button className="btn btn-success mt-2" onClick={handleSave}>
                Guardar
              </button>
              <button className="btn btn-warning mt-2 ms-2" onClick={cancelEdit}>
                Cancelar
              </button>
            </>
          ) : (
            <button className="btn btn-success mt-2" onClick={handleAdd}>
              <i className="fa fa-plus"></i>
            </button>
          )}
        </div>
      </div>
      <div className="shadow fs-11px">
        <DetalleComprobanteTabla data={data} handleEdit={handleEdit} handleDelete={handleDelete} tipoFactura={tipoFactura} />
      </div>
    </div>
  );
}
